/**
 * Hook for managing task shares (granular per-task delegation).
 * Provides a query for listing active shares and mutations for share / update /
 * revoke operations. Mirrors use-correspondence-shares; a task share NEVER
 * confers email/correspondence content access (see
 * docs/plans/pub-893-task-sharing-redo.md and the commit-1 migration).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import { TASK_QUERY_KEYS } from '@/hooks/use-tasks-helpers';
import { supabase } from '@/integrations/supabase/client';

import type { QueryClient } from '@tanstack/react-query';
import type { Database } from '@/integrations/supabase/types';

type TaskSharePermission = Database['public']['Enums']['task_share_permission_level'];
type TaskShareSource = Database['public']['Enums']['task_share_source'];
type TaskShareRow = Database['public']['Tables']['task_shares']['Row'];
type TaskShareInsert = Database['public']['Tables']['task_shares']['Insert'];

// `satisfies` ties each entry to a real `task_shares` column, so a renamed or
// misspelled column fails the build here rather than arriving `undefined` at
// runtime (the `.select(...).single()` cast below can't infer the row shape
// from a joined string). `message` is deliberately excluded - see TaskShare.
const TASK_SHARE_SELECT_COLUMN_LIST = [
  'id',
  'task_id',
  'organization_id',
  'shared_by',
  'shared_with',
  'permission',
  'share_source',
  'created_at',
  'revoked_at',
  'revoked_by',
] satisfies (keyof TaskShareRow)[];

const TASK_SHARE_SELECT_COLUMNS = TASK_SHARE_SELECT_COLUMN_LIST.join(',');

/**
 * Active task-share row as surfaced by `list_active_task_shares`. The RPC
 * intentionally omits the free-form `message` column so co-recipients cannot
 * read a grantor's constituent-context notes.
 */
export interface TaskShare {
  id: string;
  task_id: string;
  organization_id: string;
  shared_by: string;
  shared_with: string;
  permission: TaskSharePermission;
  share_source: TaskShareSource;
  created_at: string;
  revoked_at: string | null;
  revoked_by: string | null;
}

// Compile-time guard: TaskShare must stay assignment-compatible with the
// generated `task_shares` Row (minus the intentionally-stripped `message`). If
// a column is renamed/retyped in a future migration, `TaskShareFromRow` no
// longer satisfies TaskShare and the build breaks here, rather than silently
// feeding the `as TaskShare` casts below a wrong shape at runtime.
type TaskShareFromRow = Pick<TaskShareRow, keyof TaskShare>;

interface SupabaseMutationError {
  code?: string;
  message: string;
}

interface UseTaskSharesOptions {
  readonly refetchOnMount?: boolean | 'always';
  readonly staleTime?: number;
  /**
   * Gate the query off when the caller cannot manage shares. `list_active_task_shares`
   * raises 42501 for callers who are neither a native grantor nor an active
   * recipient, so an ordinary task viewer would burn a failed RPC (plus retries).
   * Callers pass their `canManageShares` capability here. Defaults to enabled.
   */
  readonly enabled?: boolean;
}

/** Query-key factory for task-share surfaces. */
export const taskShareKeys = {
  all: ['task-shares'] as const,
  shares: (orgId: string, taskId: string) =>
    [...taskShareKeys.all, 'shares', orgId, taskId] as const,
  incomingShares: (orgId: string, userId: string) =>
    [...taskShareKeys.all, 'incoming-shares', orgId, userId] as const,
  myShare: (orgId: string, taskId: string, userId: string) =>
    [...taskShareKeys.all, 'my-share', orgId, taskId, userId] as const,
};

/**
 * A recipient's own active share for a task, read directly via the
 * `task_shares_delegate` RLS policy (`USING (shared_with = auth.uid())`). Derived
 * via `Pick` from the generated `task_shares` Row so a renamed/retyped column
 * breaks the build here (mirrors {@link TaskShare}'s Row-derived guard). The
 * free-form `message` column is intentionally excluded — same isolation as
 * {@link TaskShare}. `shared_by` is retained for the recipient "Shared with you
 * by X" affordance.
 */
export type MyTaskShare = Pick<TaskShareRow, 'permission' | 'shared_by'>;

const swallow = () => false;

type ToastFn = ReturnType<typeof useToast>['toast'];

function showShareError(toast: ToastFn, title: string, message: string) {
  toast({ title, description: message, variant: 'destructive' });
}

function isActiveShareDuplicate(error: SupabaseMutationError | null) {
  return error?.code === '23505' && error.message.includes('idx_task_shares_active_unique');
}

/**
 * Invalidates the cross-cutting task feeds plus the task-share list. The
 * `myTasks` feed is cross-org (no org dimension), so it is invalidated by its
 * prefix; `myTasksByOrg`, `tasks`, and the share list all carry their scopes.
 *
 * The feeds use `refetchType: 'all'` so a backgrounded or inactive board still
 * refetches on a share grant/upgrade/revoke — an inactive board left on the
 * default `'active'` would keep rendering a stale (e.g. just-revoked) task until
 * the next focus or remount.
 */
export function invalidateTaskShareSurfaces(
  queryClient: QueryClient,
  organizationId: string,
  taskId: string,
) {
  queryClient
    .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.myTasks], refetchType: 'all' })
    .catch(swallow);
  queryClient
    .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.myTasksByOrg], refetchType: 'all' })
    .catch(swallow);
  // Scope the board key to this org ([tasks, orgId] prefix-matches the
  // [tasks, orgId, userId] board query) so a share change in one org doesn't
  // refetch a multi-org member's other-org boards.
  queryClient
    .invalidateQueries({ queryKey: [TASK_QUERY_KEYS.tasks, organizationId], refetchType: 'all' })
    .catch(swallow);
  queryClient
    .invalidateQueries({ queryKey: taskShareKeys.shares(organizationId, taskId) })
    .catch(swallow);
  // Drop the userId dimension so the per-task my-share read (the detail panel's
  // edit gate) re-runs for every recipient. Cross-session this fires on the
  // recipient's OWN realtime subscription: use-task-share-revocation-realtime
  // treats an active-row UPDATE as an access event and calls this — so a
  // grantor's view->collaborate upgrade re-gates the recipient's open panel.
  queryClient
    .invalidateQueries({ queryKey: [...taskShareKeys.all, 'my-share', organizationId, taskId] })
    .catch(swallow);
}

export function useTaskShares(
  organizationId: string | undefined,
  taskId: string | undefined,
  options: UseTaskSharesOptions = {},
) {
  return useQuery({
    queryKey: taskShareKeys.shares(organizationId ?? '', taskId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_active_task_shares', {
        p_task_id: taskId ?? '',
      });

      if (error) {
        throw new Error(error.message);
      }
      return (data ?? []) as TaskShare[];
    },
    enabled: (options.enabled ?? true) && !!organizationId && !!taskId,
    refetchOnMount: options.refetchOnMount,
    staleTime: options.staleTime ?? 300_000, // 5 minutes
  });
}

export function useHasIncomingTaskShares(
  organizationId: string | undefined,
  userId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: taskShareKeys.incomingShares(organizationId ?? '', userId ?? ''),
    queryFn: async () => {
      if (!organizationId || !userId) {
        return false;
      }
      const { count, error } = await supabase
        .from('task_shares')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('shared_with', userId)
        .is('revoked_at', null);

      if (error) {
        throw new Error(error.message);
      }
      return (count ?? 0) > 0;
    },
    enabled: enabled && !!organizationId && !!userId,
    staleTime: 30_000,
  });
}

/**
 * Reads the current user's OWN active share for a task. Unlike
 * {@link useTaskShares} (manager-only `list_active_task_shares` RPC), a recipient
 * reads their single row directly through the `task_shares_delegate` RLS policy.
 * At most one active share per (task, shared_with) — guaranteed by
 * `idx_task_shares_active_unique` — so `maybeSingle()` is safe. Serves two
 * consumers: the detail-panel edit gate reads `permission`, and the recipient
 * "Shared with you by X" affordance reads `shared_by`.
 */
export function useMyActiveTaskShare(
  organizationId: string | undefined,
  taskId: string | undefined,
  userId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: taskShareKeys.myShare(organizationId ?? '', taskId ?? '', userId ?? ''),
    queryFn: async () => {
      if (!organizationId || !taskId || !userId) {
        return null;
      }
      const { data, error } = await supabase
        .from('task_shares')
        .select('permission, shared_by')
        .eq('organization_id', organizationId)
        .eq('task_id', taskId)
        .eq('shared_with', userId)
        .is('revoked_at', null)
        .maybeSingle();

      if (error) {
        throw new Error(error.message);
      }
      return data as MyTaskShare | null;
    },
    enabled: enabled && !!organizationId && !!taskId && !!userId,
    staleTime: 30_000,
  });
}

interface ShareTaskParams {
  taskId: string;
  organizationId: string;
  sharedWith: string;
  permission: TaskSharePermission;
  message?: string;
  /**
   * Origin of the grant. Omitted -> the DB column default ('manual') applies.
   * The revoke-cascade keep-shared flow passes 'revoke_keep' so a task minted to
   * survive a correspondence revoke is attributable and reportable later.
   */
  shareSource?: TaskShareSource;
}

async function getExistingActiveShare(params: ShareTaskParams) {
  // maybeSingle (not single): the INSERT raced a concurrent soft-revoke. The
  // 23505 told us an active row existed, but it may have been revoked in the
  // window before this re-select runs, leaving zero matches. single() would
  // surface that as a confusing PGRST116 toast; maybeSingle() lets us raise a
  // clear, retryable domain error instead.
  const { data, error } = await supabase
    .from('task_shares')
    .select(TASK_SHARE_SELECT_COLUMNS)
    .eq('task_id', params.taskId)
    .eq('organization_id', params.organizationId)
    .eq('shared_with', params.sharedWith)
    .is('revoked_at', null)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error('Share was just revoked, please try again');
  }
  // Cast through the Row-derived shape so a future column drift breaks the
  // build (TaskShareFromRow must stay assignable to the TaskShare return).
  const share: TaskShare = data as TaskShareFromRow;
  return share;
}

export function useShareTask() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: async (params: ShareTaskParams) => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) {
        throw new Error('User not authenticated');
      }
      const insertPayload: TaskShareInsert = {
        task_id: params.taskId,
        organization_id: params.organizationId,
        shared_by: userId,
        shared_with: params.sharedWith,
        permission: params.permission,
        message: params.message ?? null,
      };
      // Only set share_source when provided so the column's DB default ('manual')
      // governs the common path. This keeps one mint path for both the manual
      // share-task control and the revoke-keep flow.
      if (params.shareSource) {
        insertPayload.share_source = params.shareSource;
      }
      const { data, error } = await supabase
        .from('task_shares')
        .insert(insertPayload)
        .select(TASK_SHARE_SELECT_COLUMNS)
        .single();

      if (error) {
        if (isActiveShareDuplicate(error)) {
          return await getExistingActiveShare(params);
        }
        throw new Error(error.message);
      }
      const share: TaskShare = data as TaskShareFromRow;
      return share;
    },
    onSuccess: (_data, variables) => {
      invalidateTaskShareSurfaces(queryClient, variables.organizationId, variables.taskId);
      queryClient
        .invalidateQueries({
          queryKey: taskShareKeys.incomingShares(variables.organizationId, variables.sharedWith),
        })
        .catch(swallow);
      toast({ title: t('taskShares.shared') });
    },
    onError: (error: Error) => {
      showShareError(toast, t('taskShares.errorSharing'), error.message);
    },
  });
}

interface UpdateTaskSharePermissionParams {
  shareId: string;
  organizationId: string;
  taskId: string;
  permission: TaskSharePermission;
}

export function useUpdateTaskSharePermission() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: async (params: UpdateTaskSharePermissionParams) => {
      const { error } = await supabase.rpc('update_task_share_permission', {
        p_share_id: params.shareId,
        p_permission: params.permission,
      });

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: (_data, variables) => {
      invalidateTaskShareSurfaces(queryClient, variables.organizationId, variables.taskId);
      toast({ title: t('taskShares.accessUpdated') });
    },
    onError: (error: Error) => {
      showShareError(toast, t('taskShares.errorUpdatingAccess'), error.message);
    },
  });
}

interface RevokeTaskShareParams {
  shareId: string;
  organizationId: string;
  taskId: string;
}

export function useRevokeTaskShare() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('tasks');

  return useMutation({
    mutationFn: async (params: RevokeTaskShareParams) => {
      const { error } = await supabase.rpc('revoke_task_share', {
        p_share_id: params.shareId,
      });

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: (_data, variables) => {
      invalidateTaskShareSurfaces(queryClient, variables.organizationId, variables.taskId);
      // The open task detail (`[TASK_QUERY_KEYS.task, taskId, orgId]`) is removed
      // so a recipient who just lost access cannot keep viewing a stale row.
      queryClient.removeQueries({
        queryKey: [TASK_QUERY_KEYS.task, variables.taskId, variables.organizationId],
      });
      toast({ title: t('taskShares.revoked') });
    },
    onError: (error: Error) => {
      showShareError(toast, t('taskShares.errorRevokingShare'), error.message);
    },
  });
}
